/**
 * Leave Types — Central configuration for all leave type behavior
 * Single source of truth for labels, colors, saldo impact, and categorization.
 */

import type { LeaveType } from '../stores/workforce-store';

export type LeaveCategory = 'verlof' | 'ziekte' | 'feestdag';

export interface LeaveTypeConfig {
  label: string;
  colorLight: string;       // Tailwind classes for light background badge
  colorBorder: string;      // Tailwind classes with border (for LeaveDialog buttons)
  countsAsSaldo: boolean;   // Counts as "worked" for urensaldo?
  reducesVacation: boolean; // Costs vacation hours?
  category: LeaveCategory;
}

export const LEAVE_TYPE_CONFIG: Record<LeaveType, LeaveTypeConfig> = {
  vakantie: {
    label: 'Vakantie',
    colorLight: 'bg-amber-100 text-amber-800',
    colorBorder: 'bg-amber-100 border-amber-300 text-amber-800',
    countsAsSaldo: true,
    reducesVacation: true,
    category: 'verlof',
  },
  ziekte: {
    label: 'Ziekte',
    colorLight: 'bg-red-100 text-red-800',
    colorBorder: 'bg-red-100 border-red-300 text-red-800',
    countsAsSaldo: true,
    reducesVacation: false,
    category: 'ziekte',
  },
  bijzonder: {
    label: 'Bijzonder verlof',
    colorLight: 'bg-blue-100 text-blue-800',
    colorBorder: 'bg-blue-100 border-blue-300 text-blue-800',
    countsAsSaldo: true,
    reducesVacation: false,
    category: 'verlof',
  },
  feestdag: {
    label: 'Feestdag',
    colorLight: 'bg-purple-100 text-purple-800',
    colorBorder: 'bg-purple-100 border-purple-300 text-purple-800',
    countsAsSaldo: false,
    reducesVacation: false,
    category: 'feestdag',
  },
  onbetaald: {
    label: 'Onbetaald verlof',
    colorLight: 'bg-slate-100 text-slate-700',
    colorBorder: 'bg-slate-100 border-slate-300 text-slate-800',
    countsAsSaldo: false,
    reducesVacation: false,
    category: 'verlof',
  },
  verjaardag: {
    label: 'Verjaardag',
    colorLight: 'bg-pink-100 text-pink-800',
    colorBorder: 'bg-pink-100 border-pink-300 text-pink-800',
    countsAsSaldo: true,
    reducesVacation: false,
    category: 'verlof',
  },
  geboorteverlof: {
    label: 'Geboorteverlof',
    colorLight: 'bg-cyan-100 text-cyan-800',
    colorBorder: 'bg-cyan-100 border-cyan-300 text-cyan-800',
    countsAsSaldo: true,
    reducesVacation: false,
    category: 'verlof',
  },
  aanvullend_geboorteverlof: {
    label: 'Aanv. geboorteverlof',
    colorLight: 'bg-cyan-100 text-cyan-800',
    colorBorder: 'bg-cyan-100 border-cyan-300 text-cyan-800',
    countsAsSaldo: true,
    reducesVacation: false,
    category: 'verlof',
  },
  ouderschapsverlof: {
    label: 'Ouderschapsverlof',
    colorLight: 'bg-teal-100 text-teal-800',
    colorBorder: 'bg-teal-100 border-teal-300 text-teal-800',
    countsAsSaldo: true,
    reducesVacation: false,
    category: 'verlof',
  },
};

/** Ordered list for UI rendering (LeaveDialog buttons, etc.) */
export const LEAVE_TYPES: { value: LeaveType; label: string; color: string }[] = [
  { value: 'vakantie', label: LEAVE_TYPE_CONFIG.vakantie.label, color: LEAVE_TYPE_CONFIG.vakantie.colorBorder },
  { value: 'ziekte', label: LEAVE_TYPE_CONFIG.ziekte.label, color: LEAVE_TYPE_CONFIG.ziekte.colorBorder },
  { value: 'bijzonder', label: LEAVE_TYPE_CONFIG.bijzonder.label, color: LEAVE_TYPE_CONFIG.bijzonder.colorBorder },
  { value: 'geboorteverlof', label: LEAVE_TYPE_CONFIG.geboorteverlof.label, color: LEAVE_TYPE_CONFIG.geboorteverlof.colorBorder },
  { value: 'aanvullend_geboorteverlof', label: LEAVE_TYPE_CONFIG.aanvullend_geboorteverlof.label, color: LEAVE_TYPE_CONFIG.aanvullend_geboorteverlof.colorBorder },
  { value: 'ouderschapsverlof', label: LEAVE_TYPE_CONFIG.ouderschapsverlof.label, color: LEAVE_TYPE_CONFIG.ouderschapsverlof.colorBorder },
  { value: 'onbetaald', label: LEAVE_TYPE_CONFIG.onbetaald.label, color: LEAVE_TYPE_CONFIG.onbetaald.colorBorder },
  { value: 'feestdag', label: LEAVE_TYPE_CONFIG.feestdag.label, color: LEAVE_TYPE_CONFIG.feestdag.colorBorder },
  { value: 'verjaardag', label: LEAVE_TYPE_CONFIG.verjaardag.label, color: LEAVE_TYPE_CONFIG.verjaardag.colorBorder },
];

// ── Statussen van een verlofaanvraag ─────────────────────────────

export type LeaveRequestStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'cancelled';

export const LEAVE_STATUS_LABELS: Record<LeaveRequestStatus, { label: string; color: string; mark: string }> = {
  pending: { label: 'Openstaand', color: 'bg-amber-100 text-amber-700', mark: '…' },
  approved: { label: 'Goedgekeurd', color: 'bg-emerald-100 text-emerald-600', mark: '✓' },
  rejected: { label: 'Afgewezen', color: 'bg-red-100 text-red-600', mark: '✗' },
  withdrawn: { label: 'Ingetrokken', color: 'bg-slate-100 text-slate-500', mark: '↩' },
  cancelled: { label: 'Geannuleerd', color: 'bg-orange-100 text-orange-700', mark: '⊘' },
};

export function getLeaveStatusLabel(status: string): string {
  return LEAVE_STATUS_LABELS[status as LeaveRequestStatus]?.label || status;
}

export function getLeaveStatusColor(status: string): string {
  return LEAVE_STATUS_LABELS[status as LeaveRequestStatus]?.color || 'bg-gray-100 text-gray-600';
}

// ── Helper functions ──────────────────────────────────────────────

export function getLeaveLabel(type: string): string {
  return LEAVE_TYPE_CONFIG[type as LeaveType]?.label || type;
}

export function getLeaveColor(type: string): string {
  return LEAVE_TYPE_CONFIG[type as LeaveType]?.colorLight || 'bg-gray-100 text-gray-600';
}

export function getLeaveBorderColor(type: string): string {
  return LEAVE_TYPE_CONFIG[type as LeaveType]?.colorBorder || 'bg-gray-100 border-gray-300 text-gray-600';
}

export function isHolidayLeaveType(type: string): boolean {
  return type === 'feestdag';
}

/** Filter leave entries that count towards urensaldo (countsAsSaldo: true) */
export function filterSaldoLeave<T extends { leave_type: string }>(entries: T[]): T[] {
  return entries.filter(e => LEAVE_TYPE_CONFIG[e.leave_type as LeaveType]?.countsAsSaldo === true);
}

/** Filter leave entries that reduce vacation balance */
export function filterVacationUsed<T extends { leave_type: string }>(entries: T[]): T[] {
  return entries.filter(e => LEAVE_TYPE_CONFIG[e.leave_type as LeaveType]?.reducesVacation === true);
}

/** Filter sick leave entries (category: 'ziekte') */
export function filterSickLeave<T extends { leave_type: string }>(entries: T[]): T[] {
  return entries.filter(e => LEAVE_TYPE_CONFIG[e.leave_type as LeaveType]?.category === 'ziekte');
}

/** Filter non-sick leave entries (everything except category: 'ziekte') */
export function filterNonSickLeave<T extends { leave_type: string }>(entries: T[]): T[] {
  return entries.filter(e => LEAVE_TYPE_CONFIG[e.leave_type as LeaveType]?.category !== 'ziekte');
}
